import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ShoppingBag,
  Search,
  Plus,
  Minus,
  Trash2,
  X
} from 'lucide-react';
import { useStore } from '../context/StoreContext';

export default function Purchases() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paid, setPaid] = useState(0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Store context
  const { currentStore } = useStore();

  useEffect(() => {
    loadData();
  }, [currentStore]);

  const loadData = async () => {
    try {
      const storeParam = currentStore?.id ? `?store_id=${currentStore.id}` : '';
      const [productsRes, suppliersRes, purchasesRes] = await Promise.all([
        api.get(`/products${storeParam}`),
        api.get(`/suppliers${storeParam}`),
        api.get(`/purchases${storeParam}`)
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      setPurchases(purchasesRes.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e të dhënave');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(search.toLowerCase()) ||
    product.barcode.includes(search)
  );

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        price: product.purchase_price,
        total: product.purchase_price
      }]);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
        : item
    ));
  };

  const updatePrice = (productId, newPrice) => {
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, price: newPrice, total: item.quantity * newPrice }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const total = subtotal;
  const debt = Math.max(0, total - paid);

  const handleSubmit = async () => {
    if (!selectedSupplier) {
      toast.error('Zgjidh furnitorin');
      return;
    }
    if (cart.length === 0) {
      toast.error('Shporta është bosh');
      return;
    }

    try {
      const purchaseData = {
        supplier_id: selectedSupplier.id,
        supplier_name: selectedSupplier.name,
        items: cart,
        subtotal,
        total,
        paid,
        debt,
        notes
      };
      
      if (currentStore?.id) {
        purchaseData.store_id = currentStore.id;
      }

      await api.post('/purchases', purchaseData);
      toast.success('Blerja u regjistrua me sukses');
      
      // Reset form
      setCart([]);
      setSelectedSupplier(null);
      setPaid(0);
      setNotes('');
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Gabim në regjistrimin e blerjes');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchases-page">
      {/* Header */}
      <div className="flex justify-between">
        <h2 className="text-lg font-semibold">Historia e Blerjeve</h2>
        <Button onClick={() => setIsModalOpen(true)} data-testid="new-purchase-btn">
          <Plus className="w-4 h-4 mr-2" />
          Blerje e Re
        </Button>
      </div>

      {/* Purchases Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Furnitori</TableHead>
                <TableHead>Artikuj</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paguar</TableHead>
                <TableHead className="text-right">Borxh</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    Nuk ka blerje të regjistruara
                  </TableCell>
                </TableRow>
              ) : (
                purchases.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell className="font-mono text-sm">{formatDateTime(purchase.created_at)}</TableCell>
                    <TableCell>{purchase.supplier_name}</TableCell>
                    <TableCell>{purchase.items?.length || 0}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(purchase.total)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(purchase.paid)}</TableCell>
                    <TableCell className="text-right font-mono text-orange-600">
                      {purchase.debt > 0 ? formatCurrency(purchase.debt) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New Purchase Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Blerje e Re</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Products Selection */}
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Kërko produkt..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-purchase-product"
                  />
                </div>
                <div className="border rounded-lg max-h-64 overflow-auto">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      className="w-full p-3 flex justify-between items-center hover:bg-slate-50 border-b last:border-b-0"
                      data-testid={`purchase-product-${product.id}`}
                    >
                      <span>{product.name}</span>
                      <span className="font-mono text-sm">{formatCurrency(product.purchase_price)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cart */}
              <div className="space-y-4">
                <div>
                  <Label>Furnitori *</Label>
                  <Select
                    value={selectedSupplier?.id || ''}
                    onValueChange={(value) => setSelectedSupplier(suppliers.find(s => s.id === value))}
                  >
                    <SelectTrigger data-testid="supplier-select">
                      <SelectValue placeholder="Zgjidh furnitorin" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border rounded-lg p-4 space-y-3 max-h-48 overflow-auto">
                  {cart.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">Shporta është bosh</p>
                  ) : (
                    cart.map((item) => (
                      <div key={item.product_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.product_name}</p>
                        </div>
                        <Input
                          type="number"
                          value={item.price}
                          onChange={(e) => updatePrice(item.product_id, parseFloat(e.target.value) || 0)}
                          className="w-24 font-mono text-sm"
                        />
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="w-8 text-center font-mono text-sm">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                        <span className="font-mono text-sm w-20 text-right">{formatCurrency(item.total)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => removeFromCart(item.product_id)}
                        >
                          <X className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="font-mono">{formatCurrency(total)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>Paguar:</Label>
                    <Input
                      type="number"
                      value={paid}
                      onChange={(e) => setPaid(parseFloat(e.target.value) || 0)}
                      className="font-mono"
                      data-testid="purchase-paid-input"
                    />
                  </div>
                  {debt > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>Borxh:</span>
                      <span className="font-mono font-bold">{formatCurrency(debt)}</span>
                    </div>
                  )}
                  <div>
                    <Label>Shënime</Label>
                    <Input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Shënime opsionale..."
                      data-testid="purchase-notes-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Anulo
            </Button>
            <Button onClick={handleSubmit} disabled={!selectedSupplier || cart.length === 0} data-testid="save-purchase-btn">
              Regjistro Blerjen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
